/**
 * Diagnostics, reachable from a console at `window.__surfaceBroker`.
 *
 * Two tiers, because they cost very differently:
 *
 *  - **Counters are always on.** How many payloads were built, how many a
 *    consumer reported actually delivering, and a `hint` naming the likely
 *    cause when those differ. A handful of integers, and it answers the only
 *    question anyone asks in anger — "is this surface reaching anything?"
 *  - **Full capture is opt-in** via `__surfaceBroker.enable()`. Retaining
 *    snapshots costs real memory; 200 entries of an ~8.5k payload is over a
 *    megabyte, which is not worth carrying by default.
 *
 * Building a payload and delivering one are separate events and only the
 * second is evidence — a consumer may drop it (no matching chip, wrong
 * entity, feature off) and the capture would look identical either way.
 */
import type { ViewSurfaceManifest, ViewSurfaceSnapshot } from './types';

const MAX_ENTRIES = 200;
const CAPTURE_KEY = 'surfaceBroker.capture';

/** Never throws: a locked-down storage policy must not break anything. */
function isCapturing(): boolean {
  try {
    return globalThis.localStorage?.getItem(CAPTURE_KEY) === '1';
  } catch {
    return false;
  }
}

function setCapturing(on: boolean): void {
  try {
    if (on) {
      globalThis.localStorage?.setItem(CAPTURE_KEY, '1');
    } else {
      globalThis.localStorage?.removeItem(CAPTURE_KEY);
    }
  } catch {
    /* storage unavailable — counters still work */
  }
}

export interface SurfaceBrokerDebugEntry {
  readonly at: string;
  readonly viewId: string;
  readonly entityId: string;
  readonly revision: number;
  readonly derivedAtRevision: number | null;
  readonly chars: number;
  readonly truncated: readonly string[];
  readonly unchangedSincePrevious: boolean;
  readonly snapshot: unknown;
}

const entries: SurfaceBrokerDebugEntry[] = [];
const registrationLog: string[] = [];
let lastSerialized = '';
let lastError: string | null = null;
let lastAttemptFailed = false;
let lastActionError: string | null = null;
let lastPreviewDecline: string | null = null;
let deliveries = 0;
let lastDeliveryDetail: string | null = null;

const stats = {
  built: 0,
  unchanged: 0,
  totalChars: 0,
  maxChars: 0,
  wastedCharsOnUnchanged: 0,
  everTruncated: false,
};

export interface SurfaceBrokerDebugSummary {
  readonly sends: number;
  readonly unchangedSends: number;
  readonly unchangedPct: number;
  readonly totalChars: number;
  readonly meanChars: number;
  readonly maxChars: number;
  /** Chars a perfect "skip unchanged sends" scheme would have saved. */
  readonly wastedCharsOnUnchanged: number;
  readonly everTruncated: boolean;
  readonly capturing: boolean;
  readonly capturedEntries: number;
}

export function summarizeSurfaceBroker(): SurfaceBrokerDebugSummary {
  const mean =
    stats.built === 0 ? 0 : Math.round(stats.totalChars / stats.built);
  const pct =
    stats.built === 0 ? 0 : Math.round((stats.unchanged / stats.built) * 100);
  return {
    sends: stats.built,
    unchangedSends: stats.unchanged,
    unchangedPct: pct,
    totalChars: stats.totalChars,
    meanChars: mean,
    maxChars: stats.maxChars,
    wastedCharsOnUnchanged: stats.wastedCharsOnUnchanged,
    everTruncated: stats.everTruncated,
    capturing: isCapturing(),
    capturedEntries: entries.length,
  };
}

export function getSurfaceBrokerEntries(): readonly SurfaceBrokerDebugEntry[] {
  return entries;
}

export function clearSurfaceBrokerDebug(): void {
  entries.length = 0;
  registrationLog.length = 0;
  lastSerialized = '';
  lastError = null;
  lastAttemptFailed = false;
  lastActionError = null;
  lastPreviewDecline = null;
  deliveries = 0;
  lastDeliveryDetail = null;
  stats.built = 0;
  stats.unchanged = 0;
  stats.totalChars = 0;
  stats.maxChars = 0;
  stats.wastedCharsOnUnchanged = 0;
  stats.everTruncated = false;
}

/** Several distinct causes produce an empty capture; name them explicitly. */
function hint(): string {
  if (lastAttemptFailed && lastError) {
    return `The snapshot builder threw: ${lastError}. This is a bug — capture the stack.`;
  }
  // Ahead of the delivery checks: an action that threw is a live bug, whereas
  // the counts below describe the read path, which can be perfectly healthy at
  // the same moment. Reporting "Working: 2 built, 2 delivered" while Apply is
  // throwing is exactly the misdirection this line exists to remove.
  if (lastActionError) {
    return `Read is fine, but an action threw — ${lastActionError}`;
  }
  if (registrationLog.length === 0) {
    return 'No surface has registered. Either the feature is off, or this route does not register one.';
  }
  if (stats.built === 0) {
    return 'A surface registered, but nothing has read its snapshot yet.';
  }
  if (deliveries === 0) {
    return `Built ${stats.built} payload(s) but the consumer reported delivering none. Last attempt: ${lastDeliveryDetail}`;
  }
  if (deliveries < stats.built) {
    return `Built ${stats.built}, delivered ${deliveries}. Last attempt: ${lastDeliveryDetail}`;
  }
  return `Working: ${stats.built} built, ${deliveries} delivered.`;
}

export interface SurfaceBrokerDebugStatus {
  readonly registrations: readonly string[];
  readonly payloadsRecorded: number;
  readonly payloadsDelivered: number;
  readonly lastDelivery: string | null;
  readonly lastError: string | null;
  /**
   * The last time an authority (the rater) refused to produce a value.
   *
   * Deliberately NOT `lastActionError`. A decline is an outcome, not a defect —
   * no eligible carrier is a real answer — and recording it as a throw made
   * `hint()` announce a live bug after ordinary pricing, and let a later
   * decline overwrite a genuine one. Kept because "why did it decline" is still
   * a question worth answering.
   */
  readonly lastPreviewDecline: string | null;
  /**
   * The last action dispatch that threw, with its stack.
   *
   * Separate from `lastError`, which covers only the snapshot builder. Apply
   * and Preview failures were previously reported nowhere at all: the console's
   * handler catches without binding, so the card rendered a generic refusal and
   * the error object was discarded before anything could read it.
   */
  readonly lastActionError: string | null;
  readonly hint: string;
}

export function surfaceBrokerStatus(): SurfaceBrokerDebugStatus {
  return {
    registrations: [...registrationLog],
    payloadsRecorded: stats.built,
    payloadsDelivered: deliveries,
    lastDelivery: lastDeliveryDetail,
    lastError,
    lastActionError,
    lastPreviewDecline,
    hint: hint(),
  };
}

function dump(): void {
  if (entries.length === 0) {
    return;
  }
  const blob = new Blob(
    [
      JSON.stringify(
        {
          summary: summarizeSurfaceBroker(),
          status: surfaceBrokerStatus(),
          entries,
        },
        null,
        2
      ),
    ],
    { type: 'application/json' }
  );
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `surface-broker-${entries.length}-sends.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** Attached at module load so `status()` can answer "why is nothing happening". */
function attachWindowHandle(): void {
  if (typeof window === 'undefined') {
    return;
  }
  (window as unknown as Record<string, unknown>).__surfaceBroker = {
    status: surfaceBrokerStatus,
    summary: summarizeSurfaceBroker,
    entries: getSurfaceBrokerEntries,
    clear: clearSurfaceBrokerDebug,
    dump,
    enable: () => setCapturing(true),
    disable: () => setCapturing(false),
  };
}

attachWindowHandle();

export function noteRegistration(viewKey: string, entityId: string): void {
  registrationLog.push(
    `${new Date().toISOString()} registered ${viewKey} (entityId=${entityId})`
  );
}

/** `String(unknown)` yields '[object Object]' for a thrown plain object —
 *  useless in the one place this text is read (Sonar S6551). */
function describeError(err: unknown): string {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === 'string') {
    return err;
  }
  try {
    return JSON.stringify(err) ?? 'unknown error';
  } catch {
    return 'unserialisable error';
  }
}

/**
 * Record an action dispatch that threw.
 *
 * The stack is kept rather than just the message: these throws are TypeErrors
 * from a missing handler or an absent payload key, where the message alone
 * ("x is not a function") does not say which of several call sites produced it.
 */
export function noteActionError(actionName: string, err: unknown): void {
  const stack = err instanceof Error && err.stack ? `\n${err.stack}` : '';
  lastActionError = `${actionName} threw: ${describeError(err)}${stack}`;
}

/**
 * Record an authority's refusal to produce a value.
 *
 * Separate from `noteActionError` on purpose: this never makes `hint()` claim
 * something is broken, because nothing is.
 */
export function notePreviewDecline(err: unknown): void {
  lastPreviewDecline = describeError(err);
}

export function noteSnapshotError(err: unknown): void {
  lastError = describeError(err);
  lastAttemptFailed = true;
}

/**
 * Whether the consumer actually delivered the payload. Without this, a build
 * that was silently dropped downstream is indistinguishable from one that
 * arrived.
 */
export function noteDelivery(
  delivered: boolean,
  expectedEntityId: string | null,
  candidateIds: readonly string[]
): void {
  if (delivered) {
    deliveries += 1;
  }
  lastDeliveryDetail = `delivered=${delivered} expected=${expectedEntityId} candidates=[${candidateIds.join(
    ','
  )}]`;
}

export function recordPayload(
  snapshot: ViewSurfaceSnapshot,
  capabilities: ViewSurfaceManifest
): void {
  const serialized = JSON.stringify({ snapshot, capabilities });
  const unchangedSincePrevious =
    lastSerialized !== '' && serialized === lastSerialized;
  lastSerialized = serialized;

  lastAttemptFailed = false;
  stats.built += 1;
  stats.totalChars += serialized.length;
  stats.maxChars = Math.max(stats.maxChars, serialized.length);
  if (unchangedSincePrevious) {
    stats.unchanged += 1;
    stats.wastedCharsOnUnchanged += serialized.length;
  }
  if (snapshot.truncated.length > 0) {
    stats.everTruncated = true;
  }

  if (!isCapturing()) {
    return;
  }

  entries.push({
    at: new Date().toISOString(),
    viewId: snapshot.viewId,
    entityId: snapshot.entityId,
    revision: snapshot.revision,
    derivedAtRevision: snapshot.derived?.[0]?.derivedAtRevision ?? null,
    chars: serialized.length,
    truncated: snapshot.truncated,
    unchangedSincePrevious,
    snapshot,
  });

  if (entries.length > MAX_ENTRIES) {
    entries.shift();
  }
}
